import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ArrowBackIosNewOutlined from '@mui/icons-material/ArrowBackIosNewOutlined';
import PeopleOutlineOutlined from '@mui/icons-material/PeopleOutlineOutlined';
import ExtensionOutlined from '@mui/icons-material/ExtensionOutlined';
import LinkOffOutlined from '@mui/icons-material/LinkOffOutlined';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import Header from 'shared/components/Header';
import LoadingSpinner from 'shared/components/LoadingSpinner';
import { tokens } from 'shared/theme';
import useTenantsService from 'shared/api/services/tenantsService';
import AssignServiceToTenantDialog from 'platform/catalog/pages/AssignServiceToTenantDialog';
import UnassignTenantFromService from './UnassignTenantFromService';
import {
  fetchTenantAssignmentsWithServices,
  fetchServiceCatalogRows,
  unassignTenantService,
} from 'shared/api/services/tenantAssignmentsApi';
import { buildTenantAppPath, resolveTenantSlug } from 'shared/utils/tenantPath';

const formatStatus = (t) => {
  if (!t) return '';
  if (typeof t === 'string') return t;
  if (typeof t === 'object' && t.status) return String(t.status);
  return String(t);
};

const TenantDetails = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const { tenantId } = useParams();
  const location = useLocation();
  const titleName = location.state?.tenantName;

  const { getTenantById } = useTenantsService();
  const [tenant, setTenant] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assignOpen, setAssignOpen] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [unassigning, setUnassigning] = useState(false);

  const load = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const [t, a, c] = await Promise.all([
        getTenantById(tenantId),
        fetchTenantAssignmentsWithServices(tenantId),
        fetchServiceCatalogRows(0, 500, tenantId),
      ]);
      setTenant(t);
      setAssignments(Array.isArray(a) ? a : []);
      setCatalog(Array.isArray(c) ? c : []);
    } catch (error) {
      const err =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Failed to load tenant';
      toast.error(typeof err === 'string' ? err : 'Failed to load tenant');
      setTenant(null);
      setAssignments([]);
      setCatalog([]);
    } finally {
      setLoading(false);
    }
  }, [tenantId, getTenantById]);

  useEffect(() => {
    load();
  }, [load]);

  const displayName = tenant?.name ?? titleName ?? 'Tenant';
  const statusLabel = useMemo(() => formatStatus(tenant?.status), [tenant]);

  const assignedServiceIds = useMemo(
    () => assignments.map((x) => x.serviceId).filter(Boolean),
    [assignments]
  );

  const goUsers = () => {
    const slug = resolveTenantSlug() || tenant?.name;
    const path = slug
      ? buildTenantAppPath('/tenant-users-admin', slug)
      : '/tenant-users-admin';
    navigate(path, {
      state: { registryTenantId: tenantId, tenantName: displayName },
    });
  };

  const handleOpenUnassignDialog = (assignment) => {
    setSelectedAssignment(assignment ?? null);
  };

  const handleCloseUnassignDialog = () => {
    if (unassigning) {
      return;
    }
    setSelectedAssignment(null);
  };

  const handleUnassign = async () => {
    const serviceId = selectedAssignment?.serviceId;
    if (!serviceId || !tenantId) return;
    setUnassigning(true);
    try {
      await unassignTenantService(tenantId, serviceId);
      toast.success('Service unlinked');
      setSelectedAssignment(null);
      load();
    } catch (error) {
      const err =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Failed to remove assignment';
      toast.error(typeof err === 'string' ? err : 'Failed to remove assignment');
    } finally {
      setUnassigning(false);
    }
  };

  const accent = theme.palette.secondary.main;
  const softBg = alpha(accent, 0.08);
  const cardBackgroundColor = theme.palette.background.paper;
  const registryCardBackground =
    theme.palette.mode === 'dark'
      ? theme.palette.background.paper
      : `linear-gradient(135deg, ${softBg} 0%, ${alpha(colors.primary[400], 0.35)} 100%)`;

  return (
    <Box m="20px">
      <AssignServiceToTenantDialog
        open={assignOpen}
        onClose={() => setAssignOpen(false)}
        tenantId={tenantId}
        catalogServices={catalog}
        assignedServiceIds={assignedServiceIds}
        onSuccess={load}
      />
      <UnassignTenantFromService
        open={Boolean(selectedAssignment)}
        onClose={handleCloseUnassignDialog}
        onConfirm={handleUnassign}
        submitting={unassigning}
        serviceName={selectedAssignment?.serviceName}
      />

      {/* <Box display="flex" alignItems="center" gap={1} mb={1}>
        <Button
          size="small"
          startIcon={<ArrowBackIosNewOutlined sx={{ fontSize: 14 }} />}
          onClick={() => navigate('/tenants')}
          sx={{ textTransform: 'none' }}
        >
          Back to tenants
        </Button>
      </Box> */}

      {loading && (
        <Box display="flex" justifyContent="center" py={6}>
          <LoadingSpinner />
        </Box>
      )}

      {!loading && !tenant && (
        <Typography color="error" sx={{ mt: 2 }}>
          Tenant could not be loaded.
        </Typography>
      )}

      {!loading && tenant && (
        <Stack spacing={2.5} sx={{ mt: 2 }}>
          <Card
            elevation={0}
            sx={{
              borderRadius: 2,
              border: `1px solid ${alpha(colors.grey[100], 0.15)}`,
              background: registryCardBackground,
            }}
          >
            <CardContent>
              <Typography
                variant="overline"
                letterSpacing={2}
                color="text.secondary"
                sx={{ fontWeight: 700 }}
              >
                Registry
              </Typography>
              <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap" mt={0.5}>
                <Typography variant="h3" fontWeight={700}>
                  {tenant.name}
                </Typography>
                <Chip size="small" label={statusLabel || '—'} color="secondary" variant="outlined" />
              </Stack>
              <Divider sx={{ my: 2 }} />
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                  gap: 2,
                }}
              >
                {/* <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    Tenant ID
                  </Typography>
                  <Typography variant="body2" sx={{ wordBreak: 'break-all', fontFamily: 'monospace' }}>
                    {tenant.id}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    Initialize DB
                  </Typography>
                  <Typography variant="body2">{tenant.initialize ? 'Yes' : 'No'}</Typography>
                </Box>
                <Box sx={{ gridColumn: { sm: '1 / -1' } }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    JDBC URL
                  </Typography>
                  <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                    {tenant.url || '—'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    Username
                  </Typography>
                  <Typography variant="body2">{tenant.username || '—'}</Typography>
                </Box>
                <Box sx={{ gridColumn: { sm: '1 / -1' } }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    Driver class
                  </Typography>
                  <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                    {tenant.driverClassName || '—'}
                  </Typography>
                </Box> */}
              </Box>
            </CardContent>
          </Card>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
              gap: 2,
            }}
          >
            <Card
              elevation={2}
              sx={{ borderRadius: 2, overflow: 'hidden', backgroundColor: cardBackgroundColor }}
            >
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                  <PeopleOutlineOutlined color="secondary" />
                  <Typography variant="h6" fontWeight={700} color="secondary">
                    Tenant users
                  </Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6 }}>
                  View all users who have been registered to the tenant{' '}
                </Typography>
                <Button variant="contained" color="secondary" onClick={goUsers} sx={{ textTransform: 'none' }}>
                  View users
                </Button>
              </CardContent>
            </Card>

            <Card
              elevation={2}
              sx={{ borderRadius: 2, overflow: 'hidden', backgroundColor: cardBackgroundColor }}
            >
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                  <ExtensionOutlined color="secondary" />
                  <Typography variant="h6" fontWeight={700} color="secondary">
                    Service catalog
                  </Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6 }}>
                  Link central catalog services to this tenant. Assigned services appear in the section
                  below.
                </Typography>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={() => setAssignOpen(true)}
                  sx={{ textTransform: 'none' }}
                >
                  Assign service to a tenant
                </Button>
              </CardContent>
            </Card>
          </Box>

          <Card elevation={2} sx={{ borderRadius: 2, backgroundColor: cardBackgroundColor }}>
            <CardContent>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                Services assigned to this tenant
              </Typography>
              {assignments.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  No services linked yet. Use the button above to assign.
                </Typography>
              ) : null}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, 1fr)',
                    lg: 'repeat(3, 1fr)',
                  },
                  gap: 1.5,
                }}
              >
                {assignments.map((row) => (
                  <Card
                    key={row.assignmentId || `${row.serviceId}`}
                    variant="outlined"
                    sx={{
                      borderRadius: 2,
                      backgroundColor:
                        theme.palette.mode === 'dark'
                          ? theme.palette.background.default
                          : alpha(colors.primary[500], 0.06),
                      borderColor: alpha(accent, 0.35),
                    }}
                  >
                    <CardContent sx={{ pb: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                        <Box minWidth={0}>
                          <Typography variant="subtitle1" fontWeight={700} noWrap title={row.serviceName}>
                            {row.serviceName || 'Service'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" fontFamily="monospace">
                            {row.serviceCode || '—'}
                          </Typography>
                        </Box>
                        <Tooltip title="Unlink service">
                          <IconButton
                            size="small"
                            color="warning"
                            onClick={() => handleOpenUnassignDialog(row)}
                            aria-label="Unassign service"
                          >
                            <LinkOffOutlined fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 1, minHeight: 40, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                      >
                        {row.serviceDescription?.trim() ? row.serviceDescription : 'No description'}
                      </Typography>
                      <Stack direction="row" spacing={0.75} flexWrap="wrap" mt={1}>
                        <Chip size="small" label={row.serviceStatus || '—'} variant="outlined" />
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Stack>
      )}
    </Box>
  );
};

export default TenantDetails;
